import { PropertySearchInput } from "@/components/Home/Hero/Search";
import { Button } from "@/components/ui/Button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/Dialog";
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
} from "@/components/ui/Drawer";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/Tabs";
import { useSearch } from "@/contexts/SearchContext";
import {
  PropertyListingType,
  PropertyPurposeType,
} from "@/enums/property.enum";
import { DialogTitle } from "@radix-ui/react-dialog";
import { useTranslations } from "next-intl";
import { useMediaQuery } from "usehooks-ts";
import { PropertyAmenitiesFilter } from "../Filter/Amenities";
import { PropertyAreaFilter } from "../Filter/Area";
import { PropertyBadgeFilter } from "../Filter/Badge";
import { PropertyBedRoomAndBathroomFilter } from "../Filter/BedBath";
import { PropertyFurnishingFilter } from "../Filter/Furnishing";
import { PropertyPriceFilter } from "../Filter/Price";
import { PropertyTypeFilter } from "../Filter/Property";
import { RentalPeriodFilter } from "../Filter/RentalPeriod";
import { PropertyTourFilter } from "../Filter/Tour";

interface PropertyFilterProps {
  isOpen: boolean;
  close: () => void;
}

const FilterContent = ({}: PropertyFilterProps) => {
  const t = useTranslations("shared");

  const {
    locations,
    updateLocations,
    listing,
    updateListing,
    handleSearch,
    clearFilter,
    purpose,
  } = useSearch();

  const onSearch = () => {
    handleSearch(true);
  };

  return (
    <div className="space-y-2">
      <div className="h-11">
        <PropertySearchInput value={locations} onChange={updateLocations} />
      </div>

      <div className="h-full w-full space-y-2">
        <Tabs
          value={listing.type}
          onValueChange={(value) =>
            updateListing({
              type: value as PropertyListingType,
              property: listing.property,
            })
          }
          className="w-full"
          variant="underline"
          size="sm"
        >
          <div className="overflow-x-auto lg:w-full scrollbar-thin">
            <TabsList className="lg:w-full lg:grid lg:grid-cols-[12%_12%_28%_28%_20%]">
              <TabsTrigger
                className="sm:text-base text-sm min-w-16 w-full"
                value={PropertyListingType.BUY}
              >
                {t("listing-type.buy")}
              </TabsTrigger>
              <TabsTrigger
                className="sm:text-base text-sm min-w-16 w-full"
                value={PropertyListingType.RENT}
              >
                {t("listing-type.rent")}
              </TabsTrigger>
              <TabsTrigger
                className="sm:text-base text-sm min-w-40"
                value={PropertyListingType.COMMERCIAL_BUY}
              >
                {t("listing-type.commercial-buy")}
              </TabsTrigger>
              <TabsTrigger
                className="sm:text-base text-sm min-w-40"
                value={PropertyListingType.COMMERCIAL_RENT}
              >
                {t("listing-type.commercial-rent")}
              </TabsTrigger>
              <TabsTrigger
                className="sm:text-base text-sm min-w-24"
                value={PropertyListingType.OFF_PLAN}
              >
                {t("listing-type.off-plan")}
              </TabsTrigger>
            </TabsList>
          </div>
        </Tabs>

        <div className="h-[calc(100dvh-310px)] max-h-[calc(100dvh-310px])] sm:max-h-[700px] overflow-auto mb-2 scrollbar-thin">
          <div className="flex flex-col divide-y divide-gray-300 gap-1 sm:gap-3 divide-opacity-60">
            <PropertyTypeFilter variant="button" />
            {purpose && <PropertyPriceFilter variant="plain" title />}
            <PropertyFurnishingFilter variant="button" title />
            {(listing.type === PropertyListingType.COMMERCIAL_RENT ||
              listing.type === PropertyListingType.RENT) && (
              <RentalPeriodFilter variant="button" title />
            )}
            <PropertyAreaFilter variant="plain" title />
            {purpose !== PropertyPurposeType.COMMERCIAL && (
              <PropertyBedRoomAndBathroomFilter variant="plain" title />
            )}
            <PropertyBadgeFilter variant="plain" title />
            <PropertyTourFilter variant="button" title />
            <PropertyAmenitiesFilter variant="plain" title />
          </div>
        </div>

        <div className="w-full inline-flex items-center justify-between mt-4">
          <Button variant="secondary" onClick={() => clearFilter()}>
            {t("search.clear")}
          </Button>
          <Button
            variant="default"
            onClick={() => {
              onSearch();
            }}
          >
            {t("search.search")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const PropertyFilter = ({ isOpen, close }: PropertyFilterProps) => {
  const t = useTranslations("shared");

  const isMobile = useMediaQuery("(max-width: 640px)");

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={close}>
        <DrawerContent>
          <DrawerHeader>
            <DialogTitle className="sr-only cursor-no">
              {t("search.filter")}
            </DialogTitle>
          </DrawerHeader>
          <div className=" relative px-4">
            <FilterContent isOpen={isOpen} close={close} />
          </div>
          <DrawerFooter className="!py-0.5" />
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent className="sm:max-w-2xl px-5">
        <DialogHeader>
          <DialogTitle className="sr-only">{t("search.filter")}</DialogTitle>
        </DialogHeader>
        <FilterContent isOpen={isOpen} close={close} />
      </DialogContent>
    </Dialog>
  );
};

export default PropertyFilter;
